import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { getLocalizedName } from '@/lib/utils';
import { useRoles } from '@/features/roles/hooks/useRoles';
import type { RoleType } from '@/features/roles/types';
import { billingPeriodSchema, type BillingPeriodFormValues } from '../schemas';
import { useBillingPeriodMutations, useContract } from '../hooks';

// Roles that book billable work — the only ones a package needs rates for.
const BILLABLE_ROLE_TYPES: RoleType[] = ['MD', 'PARTNER', 'LEAD', 'STAFF'];

interface TemplatePackage {
  id?: number;
  curency?: 'USD' | 'UZS';
  hours?: number;
  price?: number;
  overTimeHourlyCost?: number;
  rates?: { roleId: number; rate?: number }[];
}

interface StartBillingPeriodDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractId?: number | undefined;
  onSuccess?: () => void;
}

/**
 * Starts an INSTANCE billing period. Tariff assignment works exactly like the
 * contract form: the global-packages dropdown is only a TEMPLATE that prefills
 * the editable tariff fields (hours, price, overtime, per-role rates —
 * currency-converted into the contract's currency via its dolarRate), and the
 * backend clones the submitted values into the period's OWN single-contract
 * package — a global package is never connected.
 */
export function StartBillingPeriodDialog({
  open,
  onOpenChange,
  contractId,
  onSuccess,
}: StartBillingPeriodDialogProps) {
  const { t, i18n } = useTranslation();
  const { startBillingPeriodMutation } = useBillingPeriodMutations();

  const form = useForm<BillingPeriodFormValues>({
    resolver: zodResolver(billingPeriodSchema),
    defaultValues: {
      name: '',
      contractId: contractId,
      packageId: undefined,
      package: {
        hours: undefined,
        price: undefined,
        overTimeHourlyCost: undefined,
        rates: [],
      },
    } as unknown as BillingPeriodFormValues,
  });

  const watchedContractId = form.watch('contractId');
  const effectiveContractId = contractId ?? watchedContractId;
  // The contract supplies the target currency + dolarRate for template
  // conversion and its own tariff for the initial prefill.
  const { data: contract } = useContract(open ? effectiveContractId : undefined);
  const contractCurrency = (contract?.currency ?? 'USD') as 'USD' | 'UZS';
  const currencyLabel = contractCurrency === 'USD' ? '$' : 'UZS';

  const { data: rolesData } = useRoles({ limit: 100, page: 0, status: 'ACTIVE' });
  const billableRoles = React.useMemo(
    () =>
      (rolesData?.results ?? []).filter((r) =>
        BILLABLE_ROLE_TYPES.includes(r.type)
      ),
    [rolesData]
  );

  React.useEffect(() => {
    if (open && contractId) {
      form.setValue('contractId', contractId);
    }
  }, [open, contractId, form]);

  // Prefill the tariff from the contract's OWN package (already denominated in
  // the contract's currency — no conversion needed).
  React.useEffect(() => {
    if (!open || !contract) return;
    const hp = contract?.hourlyPackage as TemplatePackage | undefined;
    if (!hp) return;
    form.setValue('package.hours', hp.hours);
    form.setValue('package.price', hp.price);
    form.setValue('package.overTimeHourlyCost', hp.overTimeHourlyCost);
    form.setValue(
      'package.rates',
      (hp.rates ?? []).map((r) => ({ roleId: r.roleId, rate: r.rate ?? 0 }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contract]);

  // Fixed rate rows — one per billable role, realigned by roleId whenever the
  // roles load or a template/contract prefill rewrites the rates (same pattern
  // as the contract form's HourlyPackage editor).
  const watchedTemplateId = form.watch('packageId');
  React.useEffect(() => {
    if (!open || billableRoles.length === 0) return;
    const currentRates = form.getValues('package.rates') ?? [];
    form.setValue(
      'package.rates',
      billableRoles.map((role) => ({
        roleId: role.id,
        rate: currentRates.find((r) => r.roleId === role.id)?.rate ?? 0,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, billableRoles, watchedTemplateId, contract]);

  const rateRows = form.watch('package.rates') ?? [];

  const onSubmit = async (values: BillingPeriodFormValues) => {
    const finalContractId = contractId ?? values.contractId;
    if (!finalContractId) {
      return;
    }

    await startBillingPeriodMutation.mutateAsync({
      name: values.name.trim(),
      contractId: finalContractId,
      // The template `packageId` is never sent — the backend clones these
      // values into the period's own single-contract package.
      package: {
        hours: values.package?.hours,
        price: values.package?.price,
        overTimeHourlyCost: values.package?.overTimeHourlyCost,
        rates: (values.package?.rates ?? [])
          .filter((r) => r.roleId)
          .map((r) => ({ roleId: r.roleId, rate: r.rate ?? 0 })),
      },
    });

    form.reset();
    onOpenChange(false);
    onSuccess?.();
  };

  const isSubmitting = startBillingPeriodMutation.isPending;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between border-b pb-4">
          <AlertDialogTitle className="text-xl font-semibold">
            {t('new')} {t('period')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label={t('closeDialog')}
            >
              <X size={24} />
            </button>
          </AlertDialogDescription>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="mt-4 flex flex-col gap-4">
            <div className="flex flex-col gap-4">
              {/* Name */}
              <FormField
                name="name"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>{t('name')} *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={t('enterName')} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Contract (if not provided) */}
              {!contractId && (
                <FormField
                  name="contractId"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>{t('contract')} *</FormLabel>
                      <FormControl>
                        <UniversalCombobox
                          url="/contracts"
                          listUrl="/contracts/select-box"
                          labelKey="name"
                          valueKey="id"
                          selectedId={field.value}
                          onSelectItem={(item) => field.onChange(item?.['id'] ?? undefined)}
                          placeholder={t('selectContract')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Template — prefills the editable tariff below, never connected */}
              <FormField
                name="packageId"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>{t('startFromTemplate')}</FormLabel>
                    <FormControl>
                      <UniversalCombobox
                        url="/hourly-packages"
                        labelKeys={['name', 'hours', 'price']}
                        valueKey="id"
                        params={{ status: 'ACTIVE' }}
                        selectedId={field.value}
                        onSelectItem={(item) => {
                          field.onChange(item?.['id'] ?? undefined);
                          if (!item) return;
                          const template = item as TemplatePackage;
                          // Convert the template's money fields into the
                          // contract's currency using its dolarRate (USD→UZS
                          // ×rate, UZS→USD ÷rate). Hours are not money.
                          const from = template?.curency ?? 'USD';
                          const dolarRate = contract?.dolarRate;
                          const conv = (v?: number) => {
                            if (v == null) return v;
                            if (from === contractCurrency || !dolarRate) return v;
                            if (contractCurrency === 'UZS' && from === 'USD')
                              return Math.round(v * dolarRate);
                            if (contractCurrency === 'USD' && from === 'UZS')
                              return Math.round(v / dolarRate);
                            return v;
                          };
                          form.setValue('package.hours', template?.hours, { shouldValidate: true });
                          form.setValue('package.price', conv(template?.price), { shouldValidate: true });
                          form.setValue('package.overTimeHourlyCost', conv(template?.overTimeHourlyCost), { shouldValidate: true });
                          form.setValue(
                            'package.rates',
                            (template?.rates ?? []).map((r) => ({
                              roleId: r.roleId,
                              rate: conv(r.rate) ?? 0,
                            })),
                            { shouldValidate: true }
                          );
                        }}
                        placeholder={t('selectHourlyPackage')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* The period's own tariff — editable, cloned server-side */}
              <div className="bg-slate-50 rounded-lg border border-slate-200 p-4 space-y-4">
                <h4 className="text-sm font-medium text-slate-700">{t('hourlyTariff')}</h4>

                <FormField
                  name="package.hours"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('hours')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          value={field.value ?? ''}
                          onChange={(e) =>
                            field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  name="package.price"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('price')} {currencyLabel}</FormLabel>
                      <FormControl>
                        <NumericFormat
                          thousandSeparator=" "
                          allowNegative={false}
                          decimalScale={2}
                          customInput={Input}
                          value={field.value ?? ''}
                          onValueChange={(v) => field.onChange(v.floatValue ?? undefined)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  name="package.overTimeHourlyCost"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('overTimeHourlyCost')} {currencyLabel}</FormLabel>
                      <FormControl>
                        <NumericFormat
                          thousandSeparator=" "
                          allowNegative={false}
                          decimalScale={2}
                          customInput={Input}
                          value={field.value ?? ''}
                          onValueChange={(v) => field.onChange(v.floatValue ?? undefined)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Per-role overtime rates */}
                {billableRoles.length > 0 && rateRows.length === billableRoles.length && (
                  <div className="space-y-3">
                    <div>
                      <FormLabel>{t('rates')}</FormLabel>
                      <p className="text-muted-foreground mt-1 text-xs">
                        {t('perRoleOvertimeRatesHint')}
                      </p>
                    </div>
                    <div className="space-y-2">
                      {billableRoles.map((role) => {
                        // Bind by roleId, never by position — `package.rates`
                        // carries the server's own order until the realign
                        // effect runs, and binding positionally showed one
                        // role's rate under another role's name.
                        const index = rateRows.findIndex(
                          (r) => r?.roleId === role.id
                        );
                        if (index === -1) return null;
                        return (
                          <div key={role.id} className="flex items-center gap-3">
                            <span className="flex-1 text-sm">
                              {getLocalizedName(role, i18n.language)}
                            </span>
                            <FormField
                              name={`package.rates.${index}.rate`}
                              control={form.control}
                              render={({ field }) => (
                                <FormItem className="w-32">
                                  <FormControl>
                                    <NumericFormat
                                      thousandSeparator=" "
                                      allowNegative={false}
                                      decimalScale={0}
                                      customInput={Input}
                                      value={field.value ?? ''}
                                      onValueChange={(v) => field.onChange(v.floatValue ?? undefined)}
                                    />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <AlertDialogFooter className="mt-4 flex justify-end gap-2">
              <AlertDialogCancel onClick={() => onOpenChange(false)}>
                {t('cancel')}
              </AlertDialogCancel>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('saving') : `${t('save')} ${t('period')}`}
              </Button>
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
